<template>
  <div>
    <div class="flex gap-4 items-end px-0.5">
      <!-- CATEGORY NAME -->
      <div class="flex-1">
        <label class="block text-gray-600 font-semibold mb-1">Category</label>
        <BaseInput
          v-model="categoryInput"
          placeholder="e.g. TEAM1@SPRINT1"
        />
      </div>

      <!-- ADD BUTTON -->
      <BaseButton
        :disabled="!categoryInput"
        @click="hinzufuegen"
        variant="primary"
      >
        Add
      </BaseButton>

    </div>

    <!-- VORSCHLÄGE aus zwei Quellen (24.08.).
         Das Feld darüber bleibt ein FREITEXTFELD, und die Liste hier
         befüllt es nur. Vier Gründe, jeder für sich tragend:
           * der Bot läuft in der Entwicklungs- und Demo-Umgebung nicht,
           * eine Kategorie kann VOR Discord existieren (ein Sprint wird
             vorbereitet, bevor die Kanäle angelegt sind),
           * eine umbenannte oder gelöschte Kategorie bleibt
             zuordnungsbedürftig, weil die ALTEN Nachrichten den alten
             Namen tragen,
           * und der Bestand trägt Namen, die in Discord so nicht mehr
             vorkommen.
         Ein Nur-Auswahl-Feld verböte damit genau die Werte, die der
         Join tatsächlich braucht. -->
    <div class="mt-3">
      <!-- Kopfzeile: Label, Ladezustand und WOHER die Discord-Vorschläge
           stammen in EINER Zeile (30.09.) -- der Quellsatz stand vorher als
           eigener Absatz unter dem Ausfallkasten. Er bleibt sichtbar: er
           ist eine Zustandsaussage (verengt / alle Server / Bot dort nicht
           Mitglied = Grund für eine leere Liste), kein Erklärtext. -->
      <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-1.5">
        <span class="text-xs font-semibold text-gray-600">Vorschläge</span>
        <span v-if="vorschlaegeLaden" class="text-xs text-gray-400">wird geladen …</span>
        <!-- ⚠️ WOHER die Discord-Vorschläge stammen (18.09.). Derselbe Bot
             hängt auf mehreren Servern; hat das Credential einen, zeigt die
             Liste nur dessen Kategorien — hat es keinen, die aller Server,
             und gleichnamige Kategorien zweier Server fallen dann in EINEN
             Chip. Der Satz macht beides sichtbar, statt es zu verschweigen. -->
        <p v-if="vorschlagsQuelle" class="kategorie-quelle text-[11px] text-gray-500">
          {{ vorschlagsQuelle }}
        </p>
      </div>

      <!-- ⚠️ Der Ausfall wird AUSGESCHRIEBEN, nicht geschluckt. Eine
           leere Liste läse sich als „es gibt keine Kategorien" — eine
           Aussage über Discord, die hier niemand getroffen hat. -->
      <p v-if="botFehler" class="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
        {{ botFehler }}
      </p>

      <p v-if="!vorschlaegeLaden && !vorschlaege.length && !botFehler"
         class="text-xs text-gray-500 italic">
        Keine Vorschläge — weder aus Discord noch aus dem Bestand.
      </p>

      <!-- Die eigene Klasse ist ein ANKER für die Handprobe, kein Stil.
           Ohne sie traf ein Selektor `button:has-text("TEAM")` auch den
           Navigationsknopf „Teams" weiter oben auf der Seite — der
           Prüfpunkt las dann einen fremden Knopf und meldete einen
           Fehler, den es nicht gab. Dieselbe Rolle wie `messages-flow`
           in MessagesList.vue. -->
      <div v-if="vorschlaege.length" class="kategorie-vorschlaege flex flex-wrap gap-1.5">
        <button
            v-for="v in vorschlaege"
            :key="v.name"
            type="button"
            class="px-2 py-0.5 rounded-full border text-[11px] transition"
            :class="v.ausgeschlossen || !v.erreichbar
              ? 'bg-gray-100 text-gray-500 border-gray-300'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-indigo-50 hover:border-indigo-300'"
            :title="vorschlagTitel(v)"
            @click="categoryInput = v.name"
        >
          <!-- ⚠️ Der Name in Anführungszeichen, wenn er Rand-Leerzeichen
               trägt: im Bestand heißt eine Kategorie „TEAM4 " (sechs
               Zeichen), und ohne die Klammern sähe sie exakt aus wie
               TEAM4. Eine Zuordnung auf TEAM4 träfe davon keine der 28
               Nachrichten — der Join vergleicht exakt. -->
          <span v-if="v.randweiss" class="font-mono">„{{ v.name }}"</span>
          <span v-else>{{ v.name }}</span>
          <!-- Die Zahl ist die SICHTBARE, sobald das Backend die Regel
               anwenden konnte (01.10.): `anzahl` summiert alle Server, und
               „TEAM1 · 70" versprach Nachrichten, die die Session nie
               zeigt. Ohne Regel (Admin) bleibt es bei der Summe. -->
          <span v-if="v.zahl" class="text-gray-400"> · {{ v.zahl }}</span>
          <span v-if="v.herkunft === 'bestand'" class="text-gray-400"> · nur im Bestand</span>
          <span v-else-if="v.herkunft === 'discord'" class="text-gray-400"> · nur in Discord</span>
          <!-- ⚠️ Eine Kategorie OHNE Textkanal, ausgeschrieben statt
               verschwiegen (24.08., aus dem Live-Lauf). Der Testserver
               führt „Sprachkanäle" mit einem Sprachkanal darin; in der
               Liste stand sie bis hierher wie jede andere.
               Sie wird NICHT gesperrt: `on_message` filtert nicht nach
               Kanalart, der Text-Chat eines Sprachkanals trägt dieselbe
               Kategorie — von dort KANN eine Nachricht kommen, und dann
               braucht der Join die Zuordnung.
               ⚠️ Der Satz trägt die Aussage als TEXT, nicht die Farbe
               allein (WCAG 1.4.1) — dieselbe Regel wie beim
               Korrekturhinweis am Kriterium. -->
          <span v-if="v.ohneTextkanal" class="text-amber-700"> · ohne Textkanal</span>
          <!-- Server-Bezug (01.10.): Kategorienamen sind über mehrere
               Server nicht eindeutig. Genannt werden die FREMDEN Server,
               auf denen der Name vorkommt — der aktive ist der Normalfall
               und bleibt ungenannt. Das ist Herkunft, keine Bindung: die
               Zuordnung speichert nur den Namen. -->
          <span v-if="v.fremdeServer.length" class="text-gray-400"> · {{ v.fremdeServer.join(", ") }}</span>
          <span v-if="!v.erreichbar" class="text-gray-500"> · {{ VORSCHLAG_SERVER.unsichtbar }}</span>
        </button>
      </div>
    </div>

    <!-- EXISTING CATEGORIES -->
    <div class="mt-4 p-3 rounded-lg bg-gray-50 border">
      <h4 class="font-semibold text-gray-700 mb-2">Current Categories</h4>

      <!-- Eine Zeile je Zuordnung, ohne Modus (01.10.): eine Zuordnung gilt
           immer für den AKTIVEN Server des Credentials. Das Anheften an
           einen Server (18.09., Teil 2) ist zurückgebaut — Nachrichten
           anderer Server bleiben gespeichert und sind nach einem
           Rückwechsel wieder da. -->
      <ul v-if="zugeordnet.length" class="space-y-1">
        <li
          v-for="c in zugeordnet"
          :key="c.id ?? c.name"
          class="text-sm text-gray-700 flex justify-between items-center"
        >
          <div>
            ➤ <span class="font-medium">{{ c.name }}</span>
          </div>

          <button
            @click="$emit('entfernen', c)"
            class="text-red-500 hover:text-red-700 p-1 rounded"
            title="Delete category"
          >
            <TrashIcon class="w-4 h-4" />
          </button>
        </li>
      </ul>
      <p v-else class="text-gray-500 text-sm italic">No categories yet.</p>
    </div>
  </div>
</template>

<script>
/**
 * Die Zuordnung Session -> Kategorie, als eine Stelle für beide Wege.
 *
 * Sie stand bis zum 27.08. ausschließlich in SessionEdit.vue, und das
 * Anlegen-Formular kannte Kategorien überhaupt nicht: eine neu angelegte
 * Session war leer, und niemand sagte, dass hier nachzuarbeiten ist.
 *
 * ⚠️ Diese Komponente SCHREIBT nichts. Sie meldet nur `hinzufuegen` und
 * `entfernen`; wo die Zeile landet, entscheidet der Elternteil — im
 * Bearbeiten-Weg sofort über /session-categories, im Anlegen-Weg erst im
 * Create-Payload, weil es die session_id vorher nicht gibt. Zwei
 * Schreibwege in einer Komponente wären zwei Wahrheiten über denselben
 * Vorgang.
 */
import { getKategorienImBestand } from "@/live-sessions/api/session_category";
import { getBotKategorien } from "@/async-sessions/api/bot";
import { VORSCHLAEGE_QUELLE, VORSCHLAG_SERVER } from "@/async-sessions/discordServer";

import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseInput from "@/BaseComponents/BaseInput.vue";
import { TrashIcon } from "@heroicons/vue/24/solid";

export default {
  name: "KategorieWahl",
  components: { BaseButton, BaseInput, TrashIcon },
  props: {
    // Die bereits zugeordneten Kategorien. `id` trägt nur der
    // Bearbeiten-Weg (dort steht die Zeile schon in der Datenbank); der
    // Anlegen-Weg reicht Einträge ohne id durch.
    zugeordnet: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["hinzufuegen", "entfernen"],
  data() {
    return {
      categoryInput: "",
      // Die Vorschlagsliste aus zwei Quellen (24.08.). Sie ERSETZT das
      // Freitextfeld nicht, sie befüllt es.
      vorschlaege: [],
      vorschlaegeLaden: false,
      botFehler: "",
      // Woher die Discord-Hälfte stammt — ein Satz aus VORSCHLAEGE_QUELLE,
      // leer solange der Bot nicht geantwortet hat.
      vorschlagsQuelle: "",
      VORSCHLAEGE_QUELLE,
      VORSCHLAG_SERVER,
    };
  },
  methods: {
    hinzufuegen() {
      if (!this.categoryInput.trim()) return;
      // ⚠️ Gemeldet wird der UNGETRIMMTE Wert. Die Prüfung darüber wehrt
      //    nur die reine Leerzeile ab; ein Name mit Rand-Leerzeichen ist im
      //    Bestand ein echter Name („TEAM4 ", 28 Nachrichten), und der Join
      //    vergleicht exakt.
      this.$emit("hinzufuegen", this.categoryInput);
      this.categoryInput = "";
    },

    /**
     * Die Vorschläge aus BEIDEN Quellen — Discord und dem Bestand.
     *
     * ⚠️ Die beiden werden getrennt geholt und getrennt behandelt. Der
     * Bot-Aufruf darf scheitern (in der Entwicklungsumgebung ist das der
     * Regelfall), ohne die Bestandsliste mitzureißen: die ist die einzige
     * Quelle, die ohne Token funktioniert, und sie trägt die nützlichste
     * Zahl — wie viele Nachrichten an einem Namen wirklich hängen.
     *
     * ⚠️ Der Fehler wird AUSGESCHRIEBEN, nicht in die Konsole geschoben. Eine
     * leere Liste läse sich als „es gibt keine Kategorien" — eine Aussage
     * über Discord, die hier niemand getroffen hat.
     */
    async ladeVorschlaege() {
      this.vorschlaegeLaden = true;
      this.botFehler = "";

      const zusammen = new Map();
      // Server-Bezug (01.10.): Namen der Server (eigene aus dem Bestand,
      // alle aus der Bot-Antwort), der aktive Server des Credentials, und
      // ob das Backend die Sichtbarkeitsregel überhaupt anwenden konnte.
      const serverNamen = {};
      let aktiverServer = null;
      let regelBekannt = false;

      // Zuerst der Bestand: er ist die verlässliche Hälfte.
      try {
        const res = await getKategorienImBestand();
        const data = res.data || {};
        Object.assign(serverNamen, data.server_namen || {});
        aktiverServer = data.credential_server?.guild_id || null;
        // ⚠️ Nur wenn JEDE Zeile eine Zahl trägt. Der Admin bekommt
        //    `sichtbar: null` — dort gibt es keine Regel, und eine 0
        //    daraus behauptete „unsichtbar", ohne nachgesehen zu haben.
        regelBekannt = "credential_server" in data
          && (data.kategorien || []).every((k) => Number.isInteger(k.sichtbar));
        for (const k of (data.kategorien || [])) {
          zusammen.set(k.category, {
            name: k.category,
            anzahl: k.anzahl,
            sichtbar: k.sichtbar,
            server: { ...(k.je_server || {}) },
            randweiss: !!k.randweiss,
            herkunft: "bestand",
            ausgeschlossen: false,
          });
        }
      } catch (e) {
        console.error("Kategorien im Bestand nicht ladbar", e);
      }

      // Dann Discord. Was hier dazukommt, hat (noch) keine Nachrichten;
      // was schon dasteht, wird auf „beides" gehoben.
      try {
        const res = await getBotKategorien();
        this.vorschlagsQuelle = this.quelleAus(res.data || {});
        if (!aktiverServer && res.data?.verengt_auf_guild) {
          aktiverServer = String(res.data.verengt_auf_guild);
        }
        for (const g of (res.data?.guilds || [])) {
          const gid = String(g.id);
          if (g.name) serverNamen[gid] = g.name;
          for (const c of (g.categories || [])) {
            // ⚠️ Die drei Zustände von `andere_kanaele` bleiben hier
            //    getrennt: Zahl (nachgesehen), 0 (nachgesehen, nichts da),
            //    null/undefined (NICHT nachgesehen — ein älterer Bot liefert
            //    das Feld gar nicht). Nur bei einer echten Zahl darf hier
            //    etwas über andere Kanäle behauptet werden.
            const andere = Number.isInteger(c.andere_kanaele)
              ? c.andere_kanaele
              : null;
            const aus_discord = {
              textkanaele: (c.channels || []).length,
              andere,
              ohneTextkanal: (c.channels || []).length === 0,
              ausgeschlossen: !!c.ausgeschlossen,
            };
            const vorhanden = zusammen.get(c.name);
            if (vorhanden) {
              vorhanden.herkunft = "beides";
              Object.assign(vorhanden, aus_discord);
              if (!(gid in vorhanden.server)) vorhanden.server[gid] = 0;
            } else {
              zusammen.set(c.name, {
                name: c.name,
                anzahl: 0,
                sichtbar: 0,
                server: { [gid]: 0 },
                randweiss: c.name !== c.name.trim(),
                herkunft: "discord",
                ...aus_discord,
              });
            }
          }
        }
      } catch (e) {
        const status = e?.response?.status;
        // ⚠️ 401 ist ein EIGENER Satz, und er ist am 24.08. aus dem Live-Lauf
        //    entstanden. Vorher fielen 401 und 502 in denselben Zweig: die
        //    Oberfläche schrieb „nicht erreichbar", während der Bot lief,
        //    antwortete und die Netzverbindung stand — nur MESSAGE_BOT_TOKEN
        //    war leer. Wer diesen Satz liest, prüft Container und Netz und
        //    findet dort nichts. Die falsche Fährte ist teurer als gar keine.
        if (status === 401 || status === 403) {
          this.botFehler = "Der Discord-Bot lehnt die Anfrage ab (kein gültiger Ausweis). MESSAGE_BOT_TOKEN in der Oberfläche muss dem API_TOKEN des Bots entsprechen — die Liste zeigt bis dahin nur, was im Bestand vorkommt.";
        } else if (status === 503) {
          this.botFehler = "Der Discord-Bot ist noch nicht verbunden — die Liste zeigt vorerst nur, was im Bestand vorkommt.";
        } else {
          this.botFehler = "Der Discord-Bot ist nicht erreichbar — die Liste zeigt nur, was im Bestand vorkommt. Kategorien lassen sich weiter von Hand eintragen.";
        }
      }

      for (const v of zusammen.values()) {
        Object.assign(v, this.serverBild(v, { serverNamen, aktiverServer, regelBekannt }));
      }

      // Bestand zuerst und nach Häufigkeit: wer zuordnet, sucht zuerst die,
      // an der etwas hängt — und zwar an der SICHTBAREN Zahl, sonst
      // stünde ein fremder Server mit 50 Nachrichten vor dem eigenen.
      this.vorschlaege = [...zusammen.values()].sort(
        (a, b) => b.zahl - a.zahl || b.anzahl - a.anzahl || a.name.localeCompare(b.name));
      this.vorschlaegeLaden = false;
    },

    /**
     * Welcher der drei Sätze zur Bot-Antwort gehört.
     *
     * verengt_auf_guild kommt seit dem 18.09. vom Proxy: gesetzt heißt „nur
     * der Server des Credentials", und guild_vorhanden sagt, ob der Bot dort
     * überhaupt ist. Fehlt das Feld (älterer Proxy) oder ist es null, kommen
     * alle Server — und der Satz nennt ihre Zahl.
     */
    quelleAus(data) {
      const guilds = data.guilds || [];
      if (data.verengt_auf_guild) {
        if (data.guild_vorhanden === false) {
          return this.VORSCHLAEGE_QUELLE.serverUnbekannt(data.verengt_auf_guild);
        }
        return this.VORSCHLAEGE_QUELLE.einServer(guilds[0]?.name || data.verengt_auf_guild);
      }
      return this.VORSCHLAEGE_QUELLE.alleServer(guilds.length);
    },

    /**
     * Was ein Chip über seinen Server sagt (01.10.).
     *
     *   zahl         -- die sichtbare Zahl, ohne Regel die Summe
     *   fremdeServer -- Namen der Server ≠ aktiver, auf denen der Name
     *                   vorkommt; unbenannte (fremde Kurse) als EIN Eintrag
     *   erreichbar   -- zeigt die Session etwas davon? Mit Regel: sichtbare
     *                   Treffer ODER der Name steht auf dem aktiven Server
     *                   (eine neue Discord-Kategorie hat noch 0 Nachrichten
     *                   und ist trotzdem richtig). Ohne Regel: immer — es
     *                   wird nichts behauptet, was nicht gemessen ist.
     */
    serverBild(v, { serverNamen, aktiverServer, regelBekannt }) {
      const sichtbar = Number.isInteger(v.sichtbar) ? v.sichtbar : 0;
      const ids = Object.keys(v.server || {}).filter((g) => g && g !== aktiverServer);
      const benannt = [...new Set(ids.filter((g) => serverNamen[g]).map((g) => serverNamen[g]))];
      const unbenannt = ids.filter((g) => !serverNamen[g]).length;
      if (unbenannt) benannt.push(this.VORSCHLAG_SERVER.andererServer);
      return {
        zahl: regelBekannt ? sichtbar : v.anzahl,
        fremdeServer: benannt,
        erreichbar: !regelBekannt
          || sichtbar > 0
          || (!!aktiverServer && aktiverServer in (v.server || {})),
        serverNamen,
      };
    },

    vorschlagTitel(v) {
      const teile = [];
      if (v.herkunft === "beides") teile.push("In Discord und im Bestand");
      else if (v.herkunft === "discord") teile.push("In Discord, aber noch ohne Nachrichten im Bestand");
      else teile.push("Im Bestand, in Discord derzeit nicht vorhanden");
      if (v.anzahl) teile.push(`${v.anzahl} Nachrichten`);
      // Die Aufteilung je Server — dort steht auch die Summe, die der Chip
      // seit dem 01.10. nicht mehr zeigt. Auch bei NUR Altbestand, sobald
      // die sichtbare Zahl abweicht: „ohne Server: 70" erklärt dann die 0.
      const je = Object.entries(v.server || {}).filter(([, n]) => n > 0);
      const weicht = Number.isInteger(v.sichtbar) && v.sichtbar !== v.anzahl;
      if (je.length > 1 || (je.length === 1 && (je[0][0] || weicht))) {
        teile.push(je.map(([g, n]) => `${g ? (v.serverNamen?.[g] || this.VORSCHLAG_SERVER.andererServer) : this.VORSCHLAG_SERVER.ohneServer}: ${n}`).join(", "));
      }
      if (Number.isInteger(v.sichtbar) && v.anzahl) teile.push(this.VORSCHLAG_SERVER.sichtbar(v.sichtbar));
      if (!v.erreichbar) teile.push(this.VORSCHLAG_SERVER.unsichtbarWarum);
      if (v.randweiss) {
        teile.push("ACHTUNG: der Name trägt ein Leerzeichen am Rand. Die Zuordnung vergleicht exakt — ohne das Leerzeichen trifft sie nichts.");
      }
      if (v.ausgeschlossen) {
        teile.push("Diese Kategorie steht in EXCLUDED_CATEGORIES; der Bot liest dort nicht mit.");
      }
      // ⚠️ Warum sie keinen Textkanal führt — und was der Bot darüber NICHT
      //    weiß. Fehlt VIEW_CHANNEL, schickt Discord den Kanal gar nicht
      //    erst; dann zählt ihn auch `andere_kanaele` nicht mit. Der Satz
      //    behauptet deshalb nur, was gemessen ist.
      if (v.ohneTextkanal) {
        if (v.andere > 0) {
          teile.push(`Kein Textkanal — die Kategorie führt ${v.andere} Kanal/Kanäle anderer Art (etwa Sprache oder Forum). Aus dem Text-Chat eines Sprachkanals kann trotzdem eine Nachricht mit diesem Namen kommen.`);
        } else if (v.andere === 0) {
          teile.push("Kein Kanal — die Kategorie ist leer, oder der Bot darf ihre Kanäle nicht sehen. Das lässt sich von hier aus nicht unterscheiden.");
        } else {
          teile.push("Kein Textkanal. Woran das liegt, sagt dieser Bot nicht — er ist älter als die Auskunft.");
        }
      }
      return teile.join(" · ");
    },
  },

  mounted() {
    // ⚠️ BEWUSST ohne await: der Bot-Aufruf läuft gegen MESSAGE_BOT_TIMEOUT
    //    (15 s). Ein await hier hielte die ganze Seite so lange auf, und zwar
    //    genau dann, wenn der Bot nicht läuft.
    this.ladeVorschlaege();
  },
};
</script>
